import * as client from "../../../client";

export type LossRunByYear =
  | client.ParsedLossRunByYear
  | (client.ComputedLossRunByYear & {
      net_incurred: number;
      ground_up_loss: number;
      open_claims: number;
      closed_claims: number;
    })
  | client.UserGeneratedLossRunByYear
  | {
      policy_year: string;
      open_claims: 0;
      closed_claims: 0;
      description_of_losses: string;
      ground_up_loss: 0;
      net_incurred: 0;
      source: "inferred";
    };

export type Loss = client.ParsedLoss | client.UserGeneratedLoss;

export const deductibleAndNetIncurredDoNotSumToGrossLoss = (loss: Loss) => {
  // Only do the calculation if we have all values
  if (loss.deductible && loss.net_incurred && loss.ground_up_loss) {
    if (loss.ground_up_loss <= loss.deductible && loss.net_incurred === 0) {
      return false;
    }

    return (
      Math.round(loss.deductible + loss.net_incurred) !==
      Math.round(loss.ground_up_loss)
    );
  }

  return false;
};

const potentialNullToNone = (input: string | null) => {
  if (input) {
    return input;
  }
  return "None";
};

export const signatureForSourceDetails = (details: client.SourceDetails) => {
  return `${details.filename} - ${potentialNullToNone(details.sheet_name)} - ${
    details.data_type
  } - ${potentialNullToNone(details.calc_mode)} - ${potentialNullToNone(
    details.carrier
  )}`;
};

// NOTE(john) the way this function currently works, we can't trust some fields after the
// merge. For instance, differing claim types in the same year would be lost, we
// just take the first one. We can convert fields like this into an array if needed
export const mergedLossYearsForPolicyYear = (
  lossRunInfo: client.LossRunInfo,
  policyYear: string
) => {
  // Filter out disabled sources
  const lossYears =
    lossRunInfo.loss_runs_by_year[policyYear]?.filter((year) => {
      const signature = signatureForSourceDetails(year.source_details);
      return (
        signature in lossRunInfo.enabled_by_source &&
        lossRunInfo.enabled_by_source[signature]?.enabled
      );
    }) ?? [];

  const mergedYears: LossRunByYear[] = [];

  // User-generated years take precedence over everything else: they represent
  // an explicit human override of the parsed/computed numbers, so they go to
  // the front of the merged list. Callers that pick the first entry (e.g.
  // lossRunInfoToLossRunYears) will then surface the manual values.
  const userGeneratedYears = lossYears.filter(
    (year) => year.source === "user_generated"
  );
  const [userGeneratedYear] = userGeneratedYears;
  if (userGeneratedYear) {
    const userGeneratedSummary = {
      ...userGeneratedYear,
      net_incurred: userGeneratedYears
        .flatMap((year) => year.net_incurred ?? 0)
        .reduce((total, current) => total + current, 0),
      ground_up_loss: userGeneratedYears
        .flatMap((year) => year.ground_up_loss ?? 0)
        .reduce((total, current) => total + current, 0),
      open_claims: userGeneratedYears
        .flatMap((year) => year.open_claims)
        .reduce((total, current) => total + current, 0),
      closed_claims: userGeneratedYears
        .flatMap((year) => year.closed_claims)
        .reduce((total, current) => total + current, 0),
    };

    mergedYears.push(userGeneratedSummary);
  }

  // Then computed years
  const computedYears = lossYears.filter(
    (year) => year.source === "computed_from_claims"
  );
  const [computedYear] = computedYears;
  if (computedYear) {
    const computedSummary = {
      ...computedYear,
      claims: computedYears.flatMap((year) => year.claims),
      net_incurred: computedYears
        .flatMap((year) => year.claims)
        .flatMap((claim) => claim.net_incurred ?? 0)
        .reduce((total, current) => total + current, 0),
      ground_up_loss: computedYears
        .flatMap((year) => year.claims)
        .flatMap((claim) => claim.ground_up_loss ?? 0)
        .reduce((total, current) => total + current, 0),
      open_claims: computedYears
        .flatMap((year) => year.claims)
        .filter((claim) => claim.status === "open").length,
      closed_claims: computedYears
        .flatMap((year) => year.claims)
        .filter((claim) => claim.status !== "open").length,
    };

    mergedYears.push(computedSummary);
  }

  // Merging parsed years is a little trickier. We will merge all of the
  // years with carriers together, then take the one with the biggest
  // net incurred and group up loss
  const parsedYears = lossYears.filter((year) => year.source === "parsed_year");

  const parsedYearsWithCarrier = parsedYears.filter(
    (year) => year.source_details.carrier ?? "" !== ""
  );
  const parsedYearsWithoutCarrier = parsedYears.filter(
    (year) => year.source_details.carrier ?? "" === ""
  );

  const allParsedYears = parsedYearsWithoutCarrier;

  const [parsedYearWithCarrier] = parsedYearsWithCarrier;
  if (parsedYearWithCarrier) {
    const combinedCarrierYear = {
      ...parsedYearWithCarrier,
      net_incurred: parsedYearsWithCarrier
        .flatMap((year) => year.net_incurred ?? 0)
        .reduce((total, current) => total + current, 0),
      ground_up_loss: parsedYearsWithCarrier
        .flatMap((year) => year.ground_up_loss ?? 0)
        .reduce((total, current) => total + current, 0),
      open_claims: parsedYearsWithCarrier
        .flatMap((year) => year.open_claims)
        .reduce((total, current) => total + current, 0),
      closed_claims: parsedYearsWithCarrier
        .flatMap((year) => year.closed_claims)
        .reduce((total, current) => total + current, 0),
    };
    allParsedYears.push(combinedCarrierYear);
  }

  allParsedYears.sort((a, b) => {
    if (b.net_incurred !== a.net_incurred) {
      return b.net_incurred ?? 0 - (a.net_incurred ?? 0);
    }
    if (b.ground_up_loss !== a.ground_up_loss) {
      return (b.ground_up_loss ?? 0) - (a.ground_up_loss ?? 0);
    }
    return signatureForSourceDetails(a.source_details).localeCompare(
      signatureForSourceDetails(b.source_details)
    );
  });
  for (const year of allParsedYears) {
    mergedYears.push(year);
  }

  return mergedYears;
};

// Parse a policy period string of the form "YYYY-YYYY" into [startYear, endYear].
// The end year must be strictly greater than the start year — a one-year policy
// covering 2024 is "2024-2025". Returns null for any other shape (e.g.
// "all_years", malformed values, or zero-length / reversed ranges).
export const parsePolicyPeriod = (
  policyYear: string
): [number, number] | null => {
  const match = policyYear.match(/^(\d{4})-(\d{4})$/);
  if (!match) return null;
  const start = parseInt(match[1]!, 10);
  const end = parseInt(match[2]!, 10);
  if (end <= start) return null;
  return [start, end];
};

// Number of coverage years a policy period spans. A one-year policy "2022-2023"
// returns 1; a two-year policy "2023-2025" returns 2.
export const yearsInPolicyPeriod = (policyYear: string): number => {
  const parsed = parsePolicyPeriod(policyYear);
  if (!parsed) return 0;
  const [start, end] = parsed;
  return end - start;
};

// Calendar years covered by a policy period. "2023-2025" -> [2023, 2024].
export const calendarYearsCovered = (policyYear: string): number[] => {
  const parsed = parsePolicyPeriod(policyYear);
  if (!parsed) return [];
  const [start, end] = parsed;
  const years: number[] = [];
  for (let y = start; y < end; y++) years.push(y);
  return years;
};

export const lossRunInfoToLossRunYears = (lossRunInfo: client.LossRunInfo) => {
  const lossesByYear: LossRunByYear[] = [];
  for (const policyYear of Object.keys(lossRunInfo.loss_runs_by_year)) {
    const [mergedYear] = mergedLossYearsForPolicyYear(lossRunInfo, policyYear);
    if (mergedYear) {
      lossesByYear.push(mergedYear);
    }
  }

  // Sort by end year descending, then by start year descending.
  // Rows whose policy_year does not parse (e.g. "all_years") sort to the end
  // so the gap-fill logic ignores them.
  const sortedYears = lossesByYear.sort((a, b) => {
    const aParsed = parsePolicyPeriod(a.policy_year);
    const bParsed = parsePolicyPeriod(b.policy_year);
    if (!aParsed && !bParsed) return a.policy_year.localeCompare(b.policy_year);
    if (!aParsed) return 1;
    if (!bParsed) return -1;
    if (bParsed[1] !== aParsed[1]) return bParsed[1] - aParsed[1];
    return bParsed[0] - aParsed[0];
  });

  const result: LossRunByYear[] = [];

  for (let i = 0; i < sortedYears.length; i++) {
    const current = sortedYears[i]!;
    result.push(current);

    const next = sortedYears[i + 1];
    if (!next) break;

    const currentParsed = parsePolicyPeriod(current.policy_year);
    const nextParsed = parsePolicyPeriod(next.policy_year);
    if (!currentParsed || !nextParsed) continue;

    // Fill one-year gaps between next.end and current.start in descending
    // order so inferred rows slot in between the observed ones. If the
    // periods overlap or are adjacent, no rows are emitted.
    for (let s = currentParsed[0] - 1; s >= nextParsed[1]; s--) {
      result.push({
        policy_year: `${s}-${s + 1}`,
        open_claims: 0,
        closed_claims: 0,
        description_of_losses: "No reported losses",
        ground_up_loss: 0,
        net_incurred: 0,
        source: "inferred",
      });
    }
  }

  return result;
};

export const claimsFromLossYears = (lossesByYear: LossRunByYear[]): Loss[] => {
  return lossesByYear.flatMap((loss) =>
    loss.source === "computed_from_claims" ? loss.claims : []
  );
};

export const claimsFromLossRunInfo = (lossRunInfo: client.LossRunInfo) => {
  const lossYears = lossRunInfoToLossRunYears(lossRunInfo);
  return claimsFromLossYears(lossYears);
};

export type LossRunWindowStats = {
  // Averages across every calendar year in the window (real + inferred).
  avgNetIncurred: number | null;
  avgGroundUpLoss: number | null;
  // Totals across every row intersecting the window (real + inferred).
  totalNetIncurred: number | null;
  totalGroundUpLoss: number | null;
  // Distinct calendar years covered by any row intersecting the window.
  yearsCovered: number;
  // Distinct calendar years covered specifically by real (non-inferred) rows
  // — used for the "insufficient history" check.
  realYearsCovered: number;
  windowYears: number;
};

// The widest window (in calendar years) that the real (non-inferred) rows
// span, from the earliest start to the latest end. Returns 0 if no real rows
// have a parseable policy period.
export const maxWindowYearsForLossRuns = (
  lossRuns: LossRunByYear[]
): number => {
  const ranges = lossRuns
    .filter((row) => row.source !== "inferred")
    .map((row) => parsePolicyPeriod(row.policy_year))
    .filter((parsed): parsed is [number, number] => parsed !== null);
  if (ranges.length === 0) return 0;
  const earliestStart = Math.min(...ranges.map(([start]) => start));
  const latestEnd = Math.max(...ranges.map(([, end]) => end));
  return latestEnd - earliestStart;
};

// Compute per-year averages over the most recent N coverage years.
//
// The window is anchored at the latest `end` year across real (non-inferred)
// rows. A policy period [start, end) is included if it overlaps [anchor - N, anchor).
// Averages divide the summed losses by the number of distinct calendar years
// covered within the window. `realYearsCovered` ignores inferred rows so callers
// can warn when loss history is thin regardless of gap-fill.
//
// Multi-year policies that only partially overlap the window are included in
// full; the denominator still only counts years that actually fall inside the
// window, so per-year averages stay sensible in the common single-year case.
export const lossRunStatsForWindow = (
  lossRuns: LossRunByYear[],
  windowYears = 5
): LossRunWindowStats => {
  const rows = lossRuns
    .map((row) => ({ row, parsed: parsePolicyPeriod(row.policy_year) }))
    .filter(
      (entry): entry is { row: LossRunByYear; parsed: [number, number] } =>
        entry.parsed !== null
    );

  const realRows = rows.filter(({ row }) => row.source !== "inferred");
  if (realRows.length === 0) {
    return {
      avgNetIncurred: null,
      avgGroundUpLoss: null,
      totalNetIncurred: null,
      totalGroundUpLoss: null,
      yearsCovered: 0,
      realYearsCovered: 0,
      windowYears,
    };
  }

  const anchorEnd = Math.max(...realRows.map(({ parsed }) => parsed[1]));
  const windowStart = anchorEnd - windowYears;
  const windowEnd = anchorEnd;

  const inWindow = rows.filter(
    ({ parsed: [start, end] }) => start < windowEnd && end > windowStart
  );

  const yearsInWindow = new Set<number>();
  const realYearsInWindow = new Set<number>();
  let totalNetIncurred = 0;
  let totalGroundUpLoss = 0;

  for (const { row, parsed } of inWindow) {
    const [start, end] = parsed;
    for (
      let y = Math.max(start, windowStart);
      y < Math.min(end, windowEnd);
      y++
    ) {
      yearsInWindow.add(y);
      if (row.source !== "inferred") realYearsInWindow.add(y);
    }
    totalNetIncurred += row.net_incurred ?? 0;
    totalGroundUpLoss += row.ground_up_loss ?? 0;
  }

  const yearsCovered = yearsInWindow.size;
  return {
    avgNetIncurred: yearsCovered > 0 ? totalNetIncurred / yearsCovered : null,
    avgGroundUpLoss: yearsCovered > 0 ? totalGroundUpLoss / yearsCovered : null,
    totalNetIncurred: yearsCovered > 0 ? totalNetIncurred : null,
    totalGroundUpLoss: yearsCovered > 0 ? totalGroundUpLoss : null,
    yearsCovered,
    realYearsCovered: realYearsInWindow.size,
    windowYears,
  };
};

export const totalsForMonetaryFieldsForLossYearsInPolicyYear = (
  lossRunsByYear: LossRunByYear[]
): [Set<string>, Set<string>] => {
  const groundUpLossTotals = new Set<string>();
  const netInccuredTotals = new Set<string>();

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  for (const lossYear of lossRunsByYear) {
    groundUpLossTotals.add(
      lossYear.ground_up_loss
        ? formatter.format(Math.round(lossYear.ground_up_loss))
        : "N/A"
    );
    netInccuredTotals.add(
      lossYear.net_incurred
        ? formatter.format(Math.round(lossYear.net_incurred))
        : "N/A"
    );
  }

  return [groundUpLossTotals, netInccuredTotals];
};
